import React, { useMemo } from "react"
import Pagination from "react-bootstrap/Pagination"
import "./PaginationAwayter.scss"
import { Table } from "@tanstack/react-table"
import { useTheme } from "../../utils/contexts/ThemeContext"
import { useTranslation } from "react-i18next"
import { AWSelect } from "aw-select"

type Props = {
    table: Table<any>
}

const DOTS = "DOTS"

export const TablePagination: React.FC<Props> = ({ table }) => {
    const { t } = useTranslation()
    const { pageIndex, pageSize } = table.getState().pagination
    const pageCount = table.getPageCount()
    const theme = useTheme()

    const paginationRange = useMemo<(number | typeof DOTS)[]>(() => {
        const siblingCount = 1 // pages to show on each side of current
        const totalPageNumbers = 2 + 2 * siblingCount + 3 // first + last + current + siblings + 2 dots

        // Return full range when the table is small
        if (pageCount <= totalPageNumbers) {
            return [...Array(pageCount)].map((_, i) => i + 1)
        }

        const currentPage = pageIndex + 1
        const leftSibling = Math.max(currentPage - siblingCount, 2)
        const rightSibling = Math.min(currentPage + siblingCount, pageCount - 1)

        const showLeftDots = leftSibling > 2
        const showRightDots = rightSibling < pageCount - 1

        const range: (number | typeof DOTS)[] = [1]

        if (showLeftDots) {
            range.push(DOTS)
        } else {
            // no gap on the left – add the extra pages
            for (let i = 2; i < leftSibling; i++) range.push(i)
        }

        // middle block (leftSibling … rightSibling)
        for (let i = leftSibling; i <= rightSibling; i++) range.push(i)

        if (showRightDots) {
            range.push(DOTS)
        } else {
            // no gap on the right – add the extra pages
            for (let i = rightSibling + 1; i < pageCount; i++) range.push(i)
        }

        range.push(pageCount)
        return range
    }, [pageIndex, pageCount])

    const dark = theme.theme === "dark"

    return (
        <div className='d-flex justify-content-between align-items-center mt-3'>
            <div>
                <span className='counter' />
            </div>

            <div>
                <Pagination className={`dashboard-pagination ${theme.theme === "dark" ? "dark-mode" : ""} mb-0`}>
                    {/* First / Prev */}
                    <Pagination.First onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
                        <i className='bi bi-chevron-bar-left' />
                    </Pagination.First>
                    <Pagination.Prev onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                        <i className='bi bi-chevron-left' />
                    </Pagination.Prev>

                    {/* Page numbers & dots */}
                    {paginationRange.map((item, idx) =>
                        item === DOTS ? (
                            <Pagination.Ellipsis key={`dots-${idx}`} disabled />
                        ) : (
                            <Pagination.Item key={item} active={item - 1 === pageIndex} onClick={() => table.setPageIndex(item - 1)}>
                                {item}
                            </Pagination.Item>
                        )
                    )}

                    {/* Next / Last */}
                    <Pagination.Next onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                        <i className='bi bi-chevron-right' />
                    </Pagination.Next>
                    <Pagination.Last onClick={() => table.setPageIndex(pageCount - 1)} disabled={!table.getCanNextPage()}>
                        <i className='bi bi-chevron-bar-right' />
                    </Pagination.Last>
                </Pagination>
            </div>

            <div>
                <AWSelect
                    dark={dark}
                    searchable={false}
                    value={{ label: `${pageSize} / ${t("Page")}`, value: pageSize }}
                    options={[2, 5, 10, 15].map((n) => ({
                        label: `${n} / ${t("Page")}`,
                        value: n
                    }))}
                    onChange={(selected) => table.setPageSize(Number(selected?.value))}
                    menuPortalTarget={document.body}
                />
            </div>
        </div>
    )
}
